import { useCallback, useEffect, useState } from 'react';

import { DeletedTask, getDeletedTasks, getTasks } from 'services/dashboard.service';

import { CicloDoSemestre, Entrega, janelaDoCiclo } from './realizacoes-do-semestre';

const SEM_TAREFAS: Entrega[] = [];

interface Resultado {
  /** Chave da leitura que produziu `tarefas` (usuário + ciclo + janela). */
  leitura?: string;
  tarefas: Entrega[];
}

/** Realizações que o próprio usuário excluiu no ciclo (`find/entregas_excluidas`). */
export interface ExcluidasDoCiclo {
  itens: DeletedTask[];
  falhou: boolean;
}

interface ResultadoDasExcluidas extends ExcluidasDoCiclo {
  leitura?: string;
}

/** O token decide o dono; a página só manda a janela. Falha vira `falhou`, sem derrubar a lista principal. */
async function lerExcluidas(inicio: number, fim: number): Promise<ExcluidasDoCiclo> {
  try {
    const itens = await getDeletedTasks(inicio, fim);
    return { itens: Array.isArray(itens) ? itens : [], falhou: false };
  } catch {
    return { itens: [], falhou: true };
  }
}

/**
 * Realizações do usuário no ciclo inteiro: uma leitura de `find/entregas` com a janela do ciclo e sem `step`
 * (RDN-01). O efeito depende só de primitivos (AD-023); resposta de um ciclo anterior que chega tarde é
 * descartada, e a lista exposta é sempre a da leitura do ciclo atual (RDN-03). Erro não apaga a lista já
 * carregada do mesmo ciclo (RDN-18).
 *
 * Na mesma leitura, em paralelo, vêm as realizações excluídas do ciclo (RDD-10, RDD-15). `excluidas` fica
 * `undefined` até a resposta do ciclo atual chegar.
 */
export function useRealizacoesDoSemestre(chave: string | undefined, ciclo: CicloDoSemestre | undefined) {
  const janela = ciclo ? janelaDoCiclo(ciclo) : undefined;
  const inicio = janela?.start;
  const fim = janela?.end;
  const leitura = chave && ciclo ? `${chave}|${ciclo._id}|${inicio}|${fim}` : undefined;

  const [resultado, setResultado] = useState<Resultado>({ tarefas: SEM_TAREFAS });
  const [excluidas, setExcluidas] = useState<ResultadoDasExcluidas>();
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    if (!leitura || !chave) {
      setCarregando(false);
      return;
    }

    let descartada = false;
    setCarregando(true);
    setErro(false);

    if (typeof inicio === 'number' && typeof fim === 'number') {
      lerExcluidas(inicio, fim).then(lidas => {
        if (!descartada) setExcluidas({ ...lidas, leitura });
      });
    }

    getTasks(chave, inicio, fim, false)
      .then(tarefas => {
        if (!descartada) setResultado({ leitura, tarefas });
      })
      .catch(() => {
        if (!descartada) setErro(true);
      })
      .finally(() => {
        if (!descartada) setCarregando(false);
      });

    return () => {
      descartada = true;
    };
  }, [leitura, chave, inicio, fim, versao]);

  const recarregar = useCallback(() => setVersao(atual => atual + 1), []);

  return {
    tarefas: resultado.leitura === leitura ? resultado.tarefas : SEM_TAREFAS,
    carregando,
    erro,
    recarregar,
    excluidas: excluidas && excluidas.leitura === leitura ? { itens: excluidas.itens, falhou: excluidas.falhou } : undefined,
  };
}
